import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import * as Sentry from '@sentry/react';
import App from './App';
import { AuthProvider } from './components/AuthProvider';
import { PostHogProvider } from './components/PostHogProvider';
import { config, isProduction } from './config';
import { queryClient } from './queryClient';
import {
  isChunkLoadError,
  handleChunkLoadError,
  clearChunkReloadFlag,
} from './utils/chunkErrorHandler';
import './index.css';

// Initialize Sentry for error tracking (production only)
// Sentry is optional - gracefully handles missing DSN
if (config.sentryDsn && isProduction) {
  Sentry.init({
    dsn: config.sentryDsn,
    environment: config.environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text content and block all media by default for privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance monitoring - sample 10% of transactions
    tracesSampleRate: 0.1,
    // Session replay - capture 10% of sessions, 100% of sessions with errors
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Filter out PII from breadcrumbs and events
    beforeSend(event) {
      // Strip potential PII from URLs in event data
      if (event.request?.url) {
        // Remove query params that might contain PII
        const url = new URL(event.request.url);
        url.search = '';
        event.request.url = url.toString();
      }
      return event;
    },
  });
}

// WP-BUG-52: Clear stale chunk reload flags on successful page load.
// This allows future chunk errors to trigger a fresh reload attempt.
clearChunkReloadFlag();

// WP-BUG-52: Global handler for chunk load errors in dynamic imports that
// are not caught by ErrorBoundary (e.g., top-level async imports, React.lazy
// failures that surface as unhandled promise rejections).
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const error = event.reason;

  if (isChunkLoadError(error)) {
    // Report to Sentry before reload (no PII -- only error name/message/path)
    Sentry.captureException(error, {
      extra: {
        url: window.location.pathname,
      },
      tags: { type: 'chunk_load_error' },
    });

    const reloadTriggered = handleChunkLoadError(error);
    if (reloadTriggered) {
      // Prevent default rejection handling since we are reloading
      event.preventDefault();
    }
    // If retries exhausted, let the rejection propagate to ErrorBoundary
    // or the default browser handler for visibility.
  }
});

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <GoogleOAuthProvider clientId={config.googleClientId}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          {/* WP-LANDING-2: PostHogProvider handles analytics initialization
              and automatic pageview tracking */}
          <PostHogProvider>
            {/* WP-AUTH-12: AuthProvider handles session initialization
                and cross-tab logout coordination */}
            <AuthProvider>
              <App />
            </AuthProvider>
          </PostHogProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  </StrictMode>
);
